/**
 * BulkPhotoDownloadDialog — admins/supervisors download every job photo in a
 * date range as one ZIP, organized in a folder per job (Jun 2026).
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Loader2, FolderDown } from 'lucide-react';

const iso = (d) => d.toISOString().slice(0, 10);

export const BulkPhotoDownloadDialog = ({ open, onOpenChange, api, token }) => {
  const now = new Date();
  const [from, setFrom] = useState(iso(new Date(now.getFullYear(), now.getMonth(), 1)));
  const [to, setTo] = useState(iso(now));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const download = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await axios.get(`${api}/photos/bulk-zip`, {
        params: { date_from: from, date_to: to },
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
        timeout: 600000,
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `job_photos_${from}_to_${to}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      onOpenChange(false);
    } catch (e) {
      let msg = 'Download failed — please try again';
      try {
        const text = await e.response.data.text();
        msg = JSON.parse(text).detail || msg;
      } catch { /* keep default */ }
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="bulk-photo-download-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderDown className="w-5 h-5 text-[#1E88E5]" />
            Download job photos (ZIP)
          </DialogTitle>
          <DialogDescription>
            All photos uploaded in the range below, organized in a folder per job. Limit 500 photos per download.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-slate-400 mb-1 block">From</label>
            <Input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              data-testid="bulk-photo-from"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-400 mb-1 block">To</label>
            <Input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              data-testid="bulk-photo-to"
            />
          </div>
        </div>
        {error && (
          <p className="text-sm text-red-400" data-testid="bulk-photo-error">{error}</p>
        )}
        <Button
          onClick={download}
          disabled={busy || !from || !to}
          className="w-full bg-[#1E88E5] hover:bg-blue-700 text-white"
          data-testid="bulk-photo-download-btn"
        >
          {busy ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Building ZIP…</>
          ) : (
            'Download ZIP'
          )}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default BulkPhotoDownloadDialog;
